import { Paperclip } from 'lucide-react';
import { formatCurrency } from '../../../../utils/format';
import { useLanguage } from '../../../../features/language/useLanguage';
import type { PurchaseOrder, PurchaseOrderItem } from '../../../../types/purchaseOrder';
import type { GreenBeanProfile } from '../../../../types/greenBean';

interface POLineItemsPanelProps {
  po: PurchaseOrder;
  currencySymbol: string;
  greenBeanProfiles: GreenBeanProfile[];
  canWrite: boolean;
  isImage: (path: string | null | undefined) => boolean;
  onProofAction: (path: string) => void;
  onProfileClick: (item: PurchaseOrderItem) => void;
}

/** Expanded panel under a PO row: per-item quantities, shrinkage, lot tracking and totals. */
export function POLineItemsPanel({
  po,
  currencySymbol,
  greenBeanProfiles,
  canWrite,
  isImage,
  onProofAction,
  onProfileClick,
}: POLineItemsPanelProps) {
  const { t } = useLanguage();
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden w-full shadow-sm">
      <div className="px-4 py-2.5 bg-gray-50/80 border-b border-gray-200 flex justify-between items-center">
        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('purchaseOrder.table.lineItemsLots')}</h4>
        <div className="flex gap-2">
          {po.paymentProofUrl && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onProofAction(po.paymentProofUrl!);
              }}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-md ring-1 ring-inset transition-colors ${
                isImage(po.paymentProofUrl)
                  ? 'bg-primary-50 text-primary-700 hover:bg-primary-100 ring-primary-600/10'
                  : 'bg-green-50 text-green-700 hover:bg-green-100 ring-green-600/10'
              }`}
            >
              <Paperclip className="h-3.5 w-3.5" />
              {isImage(po.paymentProofUrl) ? t('purchaseOrder.actions.viewProof') : t('purchaseOrder.paymentDetails.proofOfPayment')}
            </button>
          )}

          {po.deliveryStatus === 'received' && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold text-blue-700 bg-blue-50 ring-1 ring-inset ring-blue-600/10">
              {t('purchaseOrder.table.lotTrackingActive')}
            </span>
          )}
        </div>
      </div>

      <table className="min-w-full w-full divide-y divide-gray-200">
        <thead className="bg-gray-50/80">
          <tr>
            <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('reports.shift.item')}</th>
            <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('purchaseOrder.table.qtyOrdered')}</th>
            <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('purchaseOrder.table.qtyReceived')}</th>
            <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('purchaseOrder.table.shrinkageKg')}</th>
            <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('purchaseOrder.table.shrinkagePercent')}</th>
            <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('purchaseOrder.table.lotId')}</th>
            <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('purchaseOrder.table.unitPrice')}</th>
            <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('purchaseOrder.table.total')}</th>
            <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('settingsMcp.table.actions')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {po.items.map((item, index) => (
            <tr key={index} className="hover:bg-gray-50/60 transition-colors">
              <td className="px-4 py-3 text-sm text-gray-900">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{item.name}</span>
                  {item.sampleItem && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-yellow-50 text-yellow-800 ring-1 ring-inset ring-yellow-600/20 uppercase tracking-wide">
                      {t('purchaseOrder.items.sampleItem')}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 tabular-nums text-right">
                {item.quantity}
                <span className="text-gray-400 ml-1 text-xs">kg</span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 tabular-nums text-right">
                {item.weightReceived ? (
                  <span>
                    {item.weightReceived}
                    <span className="text-gray-400 ml-1 text-xs">kg</span>
                  </span>
                ) : (
                  <span className="text-gray-300">—</span>
                )}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 tabular-nums text-right">
                {item.weightReceived ? (
                  <span>
                    {(item.quantity - item.weightReceived).toFixed(3)}
                    <span className="text-gray-400 ml-1 text-xs">kg</span>
                  </span>
                ) : (
                  <span className="text-gray-300">—</span>
                )}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 tabular-nums text-right">
                {item.weightReceived ? (
                  <span>
                    {(((item.quantity - item.weightReceived) / item.quantity) * 100).toFixed(2)}
                    <span className="text-gray-400 ml-1 text-xs">%</span>
                  </span>
                ) : (
                  <span className="text-gray-300">—</span>
                )}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm">
                {item.lotNumber ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-mono font-semibold bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-600/20">
                    {item.lotNumber}
                  </span>
                ) : (
                  <span className="text-gray-400 italic text-xs">
                    {po.deliveryStatus === 'received' ? t('purchaseOrder.table.processing') : t('purchaseOrder.table.pendingReceipt')}
                  </span>
                )}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 tabular-nums text-right">
                {formatCurrency(item.unitPrice, currencySymbol)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 tabular-nums text-right">
                {formatCurrency(item.quantity * item.unitPrice, currencySymbol)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm">
                <div className="flex flex-col gap-1 items-start">
                  {po.deliveryStatus === 'received' && item.lotId && canWrite && (
                    <button
                      type="button"
                      onClick={() => onProfileClick(item)}
                      className="bg-primary-700 text-white rounded-md p-2 text-xs font-medium hover:bg-primary-800"
                    >
                      {greenBeanProfiles.some((profile) => profile.lot_id === item.lotId)
                        ? t('purchaseOrder.table.viewGreenBeanProfile')
                        : t('purchaseOrder.table.addGreenBeanProfile')}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
